import { supabase } from "libs/supabase";
import { LoadingContext } from "providers/loading";
import { FunctionComponent, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Configurator } from "@rromikas/configurio";
import classNames from "classnames";
import Preview from "./preview";
import Editor from "./editor";
import { useUndo } from "hooks/useUndo";
import ChevronLeftIcon from "@heroicons/react/24/outline/ChevronLeftIcon";
import {
  CONFIGURATOR_BACK_BUTTON_TEST_ID,
  CONFIGURATOR_PREVIEW_BUTTON_TEST_ID,
  CONFIGURATOR_SAVE_BUTTON_TEST_ID,
} from "constants/test";

interface ConfiguratorPageProps {}

const ConfiguratorPage: FunctionComponent<ConfiguratorPageProps> = () => {
  const { id } = useParams();
  const { setLoading } = useContext(LoadingContext);
  const views = [
    { value: "editor", testid: "" },
    { value: "preview", testid: CONFIGURATOR_PREVIEW_BUTTON_TEST_ID },
  ] as const;
  const [view, setView] = useState<(typeof views)[number]["value"]>("editor");
  const [configurator, setConfigurator] = useUndo<Configurator | undefined>(undefined);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    supabase
      .from("configurators")
      .select("*")
      .eq("id", id)
      .limit(1)
      .single()
      .then((res) => {
        setLoading(false);
        if (res.data) {
          setConfigurator(res.data as Configurator);
        }
      });
  }, []);

  const onExport = () => {
    const data = JSON.stringify(configurator);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = "configurio.json";
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const onSave: React.MouseEventHandler<HTMLDivElement> = async (e) => {
    const target = e.currentTarget;
    if (!configurator) return;
    setLoading(true);
    target.innerHTML = "Saving...";
    await supabase.from("configurators").update(configurator).eq("id", configurator.id);
    target.innerHTML = "Saved!";
    setTimeout(() => {
      target.innerHTML = "Save";
    }, 2000);
    setLoading(false);
  };

  return (
    <div className="w-full h-full relative flex flex-col">
      <div className="w-full flex border-b">
        <div
          data-testid={CONFIGURATOR_BACK_BUTTON_TEST_ID}
          onClick={() => navigate(-1)}
          className="h-10 px-3 flex items-center cursor-pointer"
        >
          <ChevronLeftIcon className="w-5" />
        </div>
        <div className="flex select-none">
          {views.map((v, i) => (
            <div
              data-testid={v.testid}
              key={v.value}
              onClick={() => setView(v.value)}
              className={classNames("w-[100px] py-2 text-center cursor-pointer capitalize", {
                "bg-gray-200": view !== v.value,
                "bg-main-to text-white": view === v.value,
              })}
            >
              {v.value}
            </div>
          ))}
        </div>
        <div className="flex-grow">
          <input
            onChange={(e) => {
              setConfigurator((prev) => (!prev ? prev : { ...prev, name: e.target.value }));
            }}
            value={configurator?.name}
            className="w-full h-full outline-none px-6"
            placeholder="Configurator title..."
          ></input>
        </div>
        <div
          onClick={onExport}
          className="py-2 px-11 bg-gray-100 hover:bg-gray-200 cursor-pointer select-none active:scale-95 transform"
        >
          Export
        </div>
        <div
          data-testid={CONFIGURATOR_SAVE_BUTTON_TEST_ID}
          onClick={onSave}
          className="bg-main-to text-white py-2 px-11 cursor-pointer select-none active:scale-95 transform"
        >
          Save
        </div>
      </div>
      <div className="flex-grow h-0 w-full">
        {!configurator ? null : view === "preview" ? (
          <Preview configurator={configurator} />
        ) : (
          <Editor configurator={configurator} setConfigurator={setConfigurator}></Editor>
        )}
      </div>
    </div>
  );
};

export default ConfiguratorPage;
